<template>
	<div
		v-show="singleFeatureUsage"
		id="sidebar-feature-usage-container"
		class="'w-fit flex h-full items-center rounded-md p-0 text-sm hover:bg-won-bg-blue"
		:class="collapsed ? 'justify-center' : 'justify-between'"
	>
		<div
			v-if="!collapsed && singleFeatureUsage"
			class="w-full"
		>
			<VMenu
				placement="right-end"
				class="min-w-full"
				:triggers="['hover', 'focus']"
			>
				<div class="block rounded text-gray-700 no-underline transition">
					<div
						v-if="isSubscribe"
						class="w-full cursor-pointer flex-col rounded bg-dark-green-gradient p-3"
					>
						<div 
							class="flex items-center justify-center gap-1"
						>
							<span class="text-base font-bold text-white">
								Premium User
							</span>
                            
							<svg
								width="17"
								height="17"
								viewBox="0 0 17 17"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M6.22668 5.44L9.28002 8.5L6.22668 11.56L7.16668 12.5L11.1667 8.5L7.16668 4.5L6.22668 5.44Z"
									fill="#8897AA"
								/>
							</svg>
						</div>
					</div>

					<pdf-feature-usage-badge
						v-else-if="singleFeatureUsage"
						:is-subscribe="isSubscribe"
						:is-unlimited-for-premium="singleFeatureUsage.isUnlimitedForPremium"
						is-single-feature
						:usage="singleFeatureUsage?.usage"
						:description="features.find(item => item.name === singleFeatureUsage.name).description" 
					/>
				</div>

				<template #popper>
					<div
						class="flex w-max min-w-[280px] flex-col gap-0.5 rounded border border-won-sand-light bg-white shadow-card"
					>
						<pdf-feature-usage-badge
							v-for="item of features"
							:key="item.id"
							:is-subscribe="isSubscribe"
							:usage="item.usage"
							:description="item.description"
							:is-unlimited-for-premium="item.isUnlimitedForPremium"
						/>
					</div>
				</template>
			</VMenu>
		</div>
        
		<div
			v-else
			class="w-full"
		>
			<VMenu
				placement="right-end"
				class="flex min-w-full items-center justify-center gap-2 px-2"
				:triggers="['hover', 'focus']"
			>
				<div
					v-if="singleFeatureUsage"
					v-tooltip="{
						content: singleFeatureTooltip,
						delay: { show: collapsed ? 500 : 999999, hide: 100 }
					}"
					class="
                        mx-auto flex h-6 min-w-6 cursor-pointer items-center justify-center
                        rounded-full text-white no-underline transition
                    "
					:class="singleFeatureUsage?.usage != 0 || isSubscribe ? 'bg-won-green' : 'bg-red-500'"
				>
					<span
						v-if="isSingleFeatureUnlimited"
						class="text-[12px]"
					>
						∞
					</span>
					<span
						v-else
						class="text-[10px]"
					>
						{{ singleFeatureUsage.usage }}
					</span>
				</div>

				<template #popper>
					<div
						class="flex w-max min-w-[280px] flex-col gap-0.5 rounded border border-won-sand-light bg-white shadow-card"
					>
						<pdf-feature-usage-badge
							v-for="item of features"
							:key="item.id"
							:is-subscribe="isSubscribe"
							:usage="item.usage"
							:description="item.description"
							:is-unlimited-for-premium="item.isUnlimitedForPremium"
						/>
					</div>
				</template>
			</VMenu>
		</div>
	</div>
</template>

<script setup>
import { watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

import { useFeatureUsageStore } from '../../store/feature-usage.store';
import { useSubscriptionPlanStore } from '../../store/subscription.store';

import PdfFeatureUsageBadge from './components/badge/pdf-feature-usage-badge';

import { FEATURES_DESCRIPTION } from '../../storage';

const SIDEBAR_FEATURE_MAP = {
	job_board: 'auto_apply_v2',
};

defineProps({
	collapsed: {
		type: Boolean,
		default: false,
	}
});

const route = useRoute();
const featureUsageStore = useFeatureUsageStore();
const subscriptionStore = useSubscriptionPlanStore();
const { userFeatureUsage, singleFeatureUsage } = storeToRefs(featureUsageStore);

const sidebarFeature = computed(() => SIDEBAR_FEATURE_MAP[route.meta.feature] ?? route.meta.feature);

featureUsageStore.setActiveRouteFeature(sidebarFeature.value);
featureUsageStore.setSingleFeatureUsage(featureUsageStore.fetchFeatureUsage(sidebarFeature.value));

// Not storeToRefs(): the store exposes a plain `stripeKey`, which storeToRefs() throws on when
// VITE_STRIPE_KEY is unset (CI).
const isSubscribe = computed(() => subscriptionStore.isSubscribe);

const isSingleFeatureUnlimited = computed(() => isSubscribe.value
	&& !!FEATURES_DESCRIPTION[singleFeatureUsage.value?.name]?.isUnlimitedForPremium);

const features = computed(() => {
	return userFeatureUsage.value
		.map(({ name: featureName, usage }) => {
			let { action } = FEATURES_DESCRIPTION[featureName];
			const { description, premiumDescription, isUnlimitedForPremium } = FEATURES_DESCRIPTION[featureName];

			if (usage !== 1) {
				action = `${action}s`;
			}

			return {
				description: isSubscribe.value ? premiumDescription.replace('<<action>>', action) : description.replace('<<action>>', action),
				usage,
				name: featureName,
				isUnlimitedForPremium
			};
		});
});

// An unlimited row has no count worth showing: "Unlimited AutoApply credits left", not "0 Unlimited …".
const singleFeatureTooltip = computed(() => {
	const description = features.value.find((item) => item.name === singleFeatureUsage.value?.name)?.description;

	return isSingleFeatureUnlimited.value ? `${description} left` : `${singleFeatureUsage.value?.usage} ${description} left`;
});

watch(
	sidebarFeature,
	(newFeature) => {
		featureUsageStore.setActiveRouteFeature(newFeature);
		featureUsageStore.setSingleFeatureUsage(featureUsageStore.fetchFeatureUsage(newFeature));
	}
);
</script>
